import { SvelteMap } from 'svelte/reactivity'
import type { LotMatchPartner, MatchOverviewData, PopMatchPartner } from '$mapapp/data/data.model'
import { calculateMatchingPrices, getTransportCost } from '$mapapp/utils/match_price_calculator'
import { filterState } from '$mapapp/MapApp/FilterControl/filter.svelte'
import { sortState, type MatchingResultsSortOption } from './sortState.svelte'
import { searchMatchEntries } from './sorting.controller'

const TOP_COUNT = 5

type MatchEntry = [markerId: string, match: MatchOverviewData]
type Reference = LotMatchPartner | PopMatchPartner | undefined

// mirrors the matching list filter: keep prices that are a real, non-zero number
function priceIsNotZeroOrEmpty(price: string): boolean {
	if (!price || !parseFloat(price) || isNaN(Number(price))) return false
	if (typeof price === 'string') {
		return parseFloat(price) !== 0 && price.trim() !== 'Auf Anfrage' && price.trim() !== '-'
	}
	return price !== 0
}

/**
 * Single source of truth for the ranked matching results, shared by the list
 * (MatchingResults) and the map (LayerMatching / MarkerShell). Derives the
 * sorted+filtered ranking from `sortState` and `filterState`.
 *
 * The `matches`/`reference` inputs are injected as accessors so this module stays
 * a leaf (no import of the matching store) and avoids a circular dependency.
 */
export class MatchingResultsState {
	readonly #getMatches: () => Map<string, MatchOverviewData>
	readonly #getReference: () => Reference

	// Per-match total-cost cache. Sort comparator calls #totalCost O(N log N) times per re-sort
	// and each call runs the full calculateMatchingPrices pipeline; without caching this dominated
	// the sort-toggle latency. Keyed by the MatchOverviewData object reference so entries get Garbage Collected
	// automatically when matches are replaced by a stream update or the matching session dies.
	// The reference (origin of the matching) is invalidated explicitly because it's stable per
	// session but changes between sessions, and a stale cached cost from a previous reference
	// would silently wrong-order the list.
	#totalCostCache = new WeakMap<MatchOverviewData, number>()
	#cachedReference: Reference = undefined

	constructor(getMatches: () => Map<string, MatchOverviewData>, getReference: () => Reference) {
		this.#getMatches = getMatches
		this.#getReference = getReference
	}

	#totalCost(match: MatchOverviewData): number {
		const reference = this.#getReference()
		if (!reference) return 0
		if (reference !== this.#cachedReference) {
			this.#totalCostCache = new WeakMap()
			this.#cachedReference = reference
		}
		const cached = this.#totalCostCache.get(match)
		if (cached !== undefined) return cached
		const prices = calculateMatchingPrices(reference, match.partner, match.priceInformation)
		// HINT: a real total of 0 is a valid value, not "missing" — only fall back when it's null
		const total =
			prices.total.value != null
				? Math.abs(prices.total.value)
				: (getTransportCost(match.priceInformation.transportCost) ?? 0)
		this.#totalCostCache.set(match, total)
		return total
	}

	#comparatorFor(
		sortBy: MatchingResultsSortOption
	): (a: MatchOverviewData, b: MatchOverviewData) => number {
		switch (sortBy) {
			case 'distance':
				return (a, b) =>
					a.circulationInformation.oneWayDistance - b.circulationInformation.oneWayDistance
			case 'pricePerTon':
				return (a, b) => pricePerTonValue(a) - pricePerTonValue(b)
			case 'name':
				return (a, b) =>
					a.partner.label.localeCompare(b.partner.label, 'de', { sensitivity: 'base' })
			case 'total':
			default:
				return (a, b) => this.#totalCost(a) - this.#totalCost(b)
		}
	}

	readonly #sortedEntries: MatchEntry[] = $derived.by(() => {
		const entries = [...this.#getMatches().entries()].filter(([, match]) => {
			if (filterState.hideRecommendations && match.conditionalMatch && !match.substituteMatch) {
				return false
			}
			if (filterState.hideSubstitutes && match.substituteMatch) return false
			return !(
				filterState.hidePrices && !priceIsNotZeroOrEmpty(match.priceInformation.partnerPricePerTon)
			)
		})
		const comparator = this.#comparatorFor(sortState.sortBy)
		const direction = sortState.sortDirection === 'asc' ? 1 : -1
		// eslint-disable-next-line local/no-derived-array-mutation -- entries is a fresh local copy (spread+filter above); in-place sort is safe
		return entries.sort(([, a], [, b]) => direction * comparator(a, b))
	})

	readonly #filteredEntries: MatchEntry[] = $derived(
		searchMatchEntries(this.#sortedEntries, sortState.searchTerm)
	)

	rankedMatches: MatchOverviewData[] = $derived(this.#filteredEntries.map(([, match]) => match))

	topFive: MatchOverviewData[] = $derived(
		this.#filteredEntries.slice(0, TOP_COUNT).map(([, match]) => match)
	)

	rest: MatchOverviewData[] = $derived(
		this.#filteredEntries.slice(TOP_COUNT).map(([, match]) => match)
	)

	/** markerId → 1-based rank for every match (drives the map number badges). */
	rankByMarkerId: Map<string, number> = $derived(
		new SvelteMap(this.#filteredEntries.map(([markerId], index) => [markerId, index + 1]))
	)
}

function pricePerTonValue(match: MatchOverviewData): number {
	const value = parseFloat(match.priceInformation.partnerPricePerTon)
	return Number.isNaN(value) ? Infinity : value
}
